import { Package, ExternalLink, Trash2, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { Modal, ModalFooter } from '../../../checklists/components/modals/Modal';
import type { WatchlistItem } from '../../types';
import { getChecklistColor, getTotalQty, openProductPage, getStoreMonitoringText } from '../../utils/helpers';

interface ItemDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: WatchlistItem | null;
  onRemove: () => void;
  isRemoving: boolean;
  allChecklistIds: string[];
}

export function ItemDetailModal({
  isOpen,
  onClose,
  item,
  onRemove,
  isRemoving,
  allChecklistIds,
}: ItemDetailModalProps) {
  const { t } = useLanguage();

  if (!item) return null;

  const totalQty = getTotalQty(item);
  const hasPriceDrop = item.originalPrice != null &&
    item.currentPrice != null &&
    item.currentPrice < item.originalPrice;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={item.name}
      icon={Package}
      iconColor="primary"
      size="lg"
    >
      <div className="space-y-6">
        {/* Product Image and Code */}
        <div className="flex items-start gap-4">
          {item.imageUrl ? (
            <img
              src={item.imageUrl}
              alt={item.name}
              className="w-24 h-24 object-contain bg-gray-100 rounded-xl flex-shrink-0"
            />
          ) : (
            <div className="w-24 h-24 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Package className="w-10 h-10 text-gray-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-500 font-mono">{item.productCode}</p>
            <button
              onClick={() => openProductPage(item.productCode)}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              <ExternalLink className="w-4 h-4" />
              {t('watchlist.viewOnIkea', 'View on IKEA')}
            </button>
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-4">
          {/* Current Price */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-500 mb-1">
              {t('watchlist.currentPrice', 'Current Price')}
            </p>
            {item.currentPrice != null ? (
              <div>
                <p className={`text-xl font-bold ${hasPriceDrop ? 'text-green-600' : 'text-gray-900'}`}>
                  ${item.currentPrice.toFixed(2)}
                </p>
                {hasPriceDrop && (
                  <p className="text-sm text-gray-400 line-through">
                    ${item.originalPrice?.toFixed(2)}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xl font-bold text-gray-400">
                {t('common.notAvailable', 'N/A')}
              </p>
            )}
          </div>

          {/* Total Qty Needed */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-500 mb-1">
              {t('watchlist.totalQtyNeeded', 'Total Qty Needed')}
            </p>
            <p className="text-xl font-bold text-gray-900">{totalQty}</p>
          </div>

          {/* Availability */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-500 mb-1">
              {t('watchlist.availability', 'Availability')}
            </p>
            <span
              className={`inline-flex px-2.5 py-1 text-sm font-medium rounded-full ${
                item.availability === 'In Stock'
                  ? 'bg-green-100 text-green-800'
                  : item.availability === 'Low Stock'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {item.availability || t('watchlist.unknown', 'Unknown')}
            </span>
          </div>

          {/* Store Monitoring */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-500 mb-1">
              {t('watchlist.monitoring', 'Monitoring')}
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {getStoreMonitoringText(item.stores, t)}
            </p>
          </div>
        </div>

        {/* Source Checklists */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-3">
            {t('watchlist.source', 'Source')}
          </p>
          <div className="flex flex-wrap gap-2">
            {item.checklists && item.checklists.length > 0 ? (
              item.checklists.map(c => (
                <span
                  key={c.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-white"
                  style={{ backgroundColor: getChecklistColor(c.id, allChecklistIds) }}
                >
                  {c.name}
                  {c.quantity_needed && c.quantity_needed > 1 && (
                    <span className="opacity-80">{t('watchlist.qtyFormat', '(x{{qty}})', { qty: c.quantity_needed })}</span>
                  )}
                </span>
              ))
            ) : (
              <span className="inline-flex px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-700 text-white">
                {t('watchlist.manual', 'Manual')}
              </span>
            )}
          </div>
        </div>

        <ModalFooter>
          <button
            onClick={onRemove}
            disabled={isRemoving}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 border border-red-200 rounded-xl hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isRemoving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            {t('watchlist.removeFromWatchlist', 'Remove from Watchlist')}
          </button>
        </ModalFooter>
      </div>
    </Modal>
  );
}
